import React, { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Chip,
  Grid,
  Box,
  Stack,
  CircularProgress,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import PersonIcon from '@mui/icons-material/Person';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { EntityType } from 'shared/enums';
import { DatasetsService } from 'services';
import { useParams } from 'react-router-dom';
import { Utils } from 'shared/helpers';

interface UpdatedField {
  old?: any;
  new?: any;
}

interface HistoryData {
  _id: string;
  action: 'created' | 'updated' | 'status_changed' | 'linked' | 'unlinked';
  createdAt: string;
  entityType: string;
  entityId: string;
  userName?: string;
  actionId?: {
    _id: string;
    name: string;
  };
  previousStatus?: string | null;
  newStatus?: string;
  updatedFields?: {
    [key: string]: UpdatedField | undefined;
    // Dataset fields
    datasetName?: UpdatedField;
    riskTier?: UpdatedField;
    owningTeam?: UpdatedField;
    sourceType?: UpdatedField;
    purposeOfUse?: UpdatedField;
    lifecycleStage?: UpdatedField;
    dataSensitivityClassification?: UpdatedField;
    // AI System fields
    organizationUnit?: UpdatedField;
    stakeholder?: UpdatedField;
    solutionName?: UpdatedField;
    solutionOwner?: UpdatedField;
    classification?: UpdatedField;
    // Risk Register fields
    riskTitle?: UpdatedField;
    riskDescription?: UpdatedField;
    affectedStakeholders?: UpdatedField;
    mitigationPlan?: UpdatedField;
    residualRiskOwner?: UpdatedField;
    riskType?: UpdatedField;
    likelihood?: UpdatedField;
    impact?: UpdatedField;
    mitigationStatus?: UpdatedField;
    residualRiskLevel?: UpdatedField;
    datasetId?: UpdatedField;
    aisystemId?: UpdatedField;
    // Linked fields
    linkedType?: UpdatedField;
    linkedId?: UpdatedField;
    linkedName?: UpdatedField;

    // Incident fields
    severity?: UpdatedField;
    title?: UpdatedField;
    description?: UpdatedField;
    incident_source?: UpdatedField;
    incident_type?: UpdatedField;
    business_owner?: UpdatedField;
    contains_personal_data?: UpdatedField;
    assigned_to?: UpdatedField;
    status?: UpdatedField;
    data_subjects_impacted?: UpdatedField;
    reported_by?: UpdatedField;
    _details?: UpdatedField;

    // Vulnerability fields
    vulnerability_category?: UpdatedField;
    risk_rating?: UpdatedField;
    severity_score?: UpdatedField;
    residual_risk?: UpdatedField;
    exploitability_details?: UpdatedField;
    compliance_notes?: UpdatedField;
    confidentiality_classification?: UpdatedField;
    lifecycle_stage_impacted?: UpdatedField;
    environment?: UpdatedField;
    target_remediation_date?: UpdatedField;
    verification_evidence?: UpdatedField;
    reporter_source?: UpdatedField;
    owner_assignee?: UpdatedField;
    business_impact?: UpdatedField;
    deployment_endpoints?: UpdatedField;
    riskId?: UpdatedField;
    incidentId?: UpdatedField;
  };
  changeSummary?: string;
}

interface AuditTrailProps {
  title: string;
  entityType: EntityType;
  onError?: (error: any) => void;
}

const AuditTrail: React.FC<AuditTrailProps> = ({
  title,
  entityType,
  onError,
}) => {
  const [historyData, setHistoryData] = useState<HistoryData[] | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const { aiSystemId, recordId, riskId, incidentId, vulnerabilityId } =
    useParams<{
      aiSystemId?: string;
      recordId?: string;
      riskId?: string;
      incidentId?: string;
      vulnerabilityId?: string;
    }>();

  const entityId = Utils.getEntityId(
    entityType,
    aiSystemId,
    recordId,
    riskId,
    incidentId,
    vulnerabilityId
  );

  const formatTimestamp = (timestamp: string): string => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const getFieldDisplayName = (field: string, entityType?: string): string => {
    const datasetFieldMap: Record<string, string> = {
      riskTier: 'Risk Tier',
      owningTeam: 'Owning Team',
      sourceType: 'Source Type',
      datasetName: 'Dataset Name',
      purposeOfUse: 'Purpose Of Use',
      lifecycleStage: 'Lifecycle Stage',
      dataSensitivityClassification: 'Data Sensitivity Classification',
      contactPerson: 'Contact Person',
      accessControls: 'Access Controls',
      sharingRestrictions: 'Sharing Restrictions',
    };

    const aiSystemFieldMap: Record<string, string> = {
      approvalStatus: 'Approval Status',
      approvalDate: 'Approval Date',
      organizationUnit: 'Organisation Unit',
      stakeholder: 'Stakeholder',
      solutionName: 'Solution Name',
      solutionOwner: 'Solution Owner',
      classification: 'Classification',
    };

    const riskRegisterFieldMap: Record<string, string> = {
      riskTitle: 'Risk Title',
      riskDescription: 'Risk Description',
      affectedStakeholders: 'Affected Stakeholders',
      mitigationPlan: 'Mitigation Plan',
      residualRiskOwner: 'Residual Risk Owner',
      riskType: 'Risk Type',
      likelihood: 'Likelihood',
      impact: 'Impact',
      mitigationStatus: 'Mitigation Status',
      residualRiskLevel: 'Residual Risk Level',
      datasetId: 'Dataset ID',
      aisystemId: 'AI System ID',
    };

    const linkedFieldMap: Record<string, string> = {
      linkedType: 'Linked Entity Type',
      linkedId: 'Linked Entity ID',
      linkedName: 'Linked Entity Name',
    };

    const incidentFieldMap: Record<string, string> = {
      severity: 'Severity',
      title: 'Title',
      description: 'Description',
      incident_source: 'Incident Source',
      incident_type: 'Incident Type',
      business_owner: 'Business Owner',
      contains_personal_data: 'Contains Personal Data',
      assigned_to: 'Assigned To',
      status: 'Status',
    };

    const vulnerabilityFieldMap: Record<string, string> = {
      title: 'Title',
      description: 'Description',
      vulnerability_category: 'Vulnerability Category',
      risk_rating: 'Risk Rating',
      severity_score: 'Severity Score',
      residual_risk: 'Residual Risk',
      exploitability_details: 'Exploitability Details',
      compliance_notes: 'Compliance Notes',
      confidentiality_classification: 'Confidentiality Classification',
    };

    if (entityType === EntityType.AI_SYSTEM) {
      return aiSystemFieldMap[field] || '';
    }
    if (entityType === EntityType.RISK_REGISTER) {
      return riskRegisterFieldMap[field] || '';
    }
    if (field.startsWith('linked')) {
      return linkedFieldMap[field] || '';
    }
    if (entityType === EntityType.INCIDENT) {
      return incidentFieldMap[field] || '';
    }
    if (entityType === EntityType.VULNERABILITY) {
      return vulnerabilityFieldMap[field] || '';
    }
    return datasetFieldMap[field] || '';
  };

  const formatValue = (value: any): string => {
    if (value === null || value === undefined) return 'Not set';
    if (Array.isArray(value)) return value.join(', ');
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    return String(value);
  };

  const formatCreatedValue = (value: any): string => {
    if (value === null || value === undefined) return '';
    if (Array.isArray(value)) return '';
    if (typeof value === 'object') return '';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    return String(value);
  };

  const getActionIcon = (history: HistoryData): React.ReactElement => {
    if (history.action === 'linked') {
      return <LinkIcon />;
    }
    if (history.action === 'unlinked') {
      return <LinkOffIcon />;
    }
    if (history.action === 'updated' || history.action === 'status_changed') {
      return <EditIcon />;
    }
    return <AddIcon />;
  };

  const fetchHistory = (): void => {
    if (!entityId) return;

    setLoading(true);
    DatasetsService.getDatasetHistory(entityType, entityId)
      .then((response) => {
        const historyArray = response?.data?.data || response?.data || [];
        setHistoryData(historyArray);
        setLoading(false);
      })
      .catch((err) => {
        console.error('ERROR FOUND', err);
        setHistoryData(null);
        setLoading(false);
        if (onError) onError(err);
      });
  };

  useEffect(() => {
    if (entityId && entityType) {
      fetchHistory();
    }
  }, [entityId, entityType]);

  if (loading) {
    return (
      <Box className="empty-content">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className="record-container">
      <Box className="record-container-title">
        <Typography variant="h4">{title}</Typography>
      </Box>

      <Box className="container-wrapper">
        {!historyData || historyData.length === 0 ? (
          <Box className="container-wrapper">
            <Typography>No history records found.</Typography>
          </Box>
        ) : (
          historyData?.map((history: HistoryData) => (
            <Card
              key={history._id}
              variant="outlined"
              className={`audit-trail-container ${
                history.action === 'created' ? 'create' : ''
              }`}
              sx={{ mb: 2 }}
            >
              <CardContent>
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  flexWrap="wrap"
                >
                  {getActionIcon(history)}

                  <Chip label={history.changeSummary} />

                  <Stack direction="row" spacing={1} alignItems="center">
                    <PersonIcon fontSize="small" />
                    <Typography>
                      {history.userName || history.actionId?.name || 'Unknown'}
                    </Typography>
                  </Stack>

                  <Stack direction="row" spacing={1} alignItems="center">
                    <CalendarTodayIcon fontSize="small" />
                    <Typography>
                      {formatTimestamp(history.createdAt)}
                    </Typography>
                  </Stack>
                </Stack>

                {(history.action === 'linked' ||
                  history.action === 'unlinked') &&
                  history.updatedFields && (
                    <CardContent
                      className="audit-trail-content"
                      sx={{ px: 0, pb: 0, pt: 2 }}
                    >
                      <Stack direction="column" spacing={2}>
                        <Typography variant="body2" fontWeight={600}>
                          {history.action === 'linked'
                            ? 'Linked Details:'
                            : 'Unlinked Details:'}
                        </Typography>

                        {history.updatedFields?.linkedName && (
                          <Box>
                            <Typography variant="body2" fontWeight={600}>
                              Linked {history.updatedFields.linkedType?.new} :{' '}
                              {formatValue(
                                history.action === 'linked'
                                  ? history.updatedFields.linkedName.new
                                  : history.updatedFields.linkedName.old
                              )}
                            </Typography>
                          </Box>
                        )}
                      </Stack>
                    </CardContent>
                  )}

                {/* STATUS CHANGED SECTION */}
                {history.action === 'status_changed' &&
                  history.updatedFields &&
                  history.updatedFields.approvalStatus && (
                    <CardContent
                      className="audit-trail-content"
                      sx={{ px: 0, pb: 0 }}
                    >
                      <Stack
                        direction="row"
                        spacing={2}
                        alignItems="center"
                        flexWrap="wrap"
                        mt={2}
                      >
                        <Typography variant="body1">
                          Status changed from
                        </Typography>
                        <Chip
                          label={
                            history.updatedFields.approvalStatus.old ||
                            'Pending'
                          }
                        />
                        <Typography variant="body1">to</Typography>
                        <Chip
                          label={
                            history.updatedFields.approvalStatus.new ||
                            'Approved'
                          }
                          color={
                            history.updatedFields.approvalStatus.new ===
                            'Approved'
                              ? 'success'
                              : 'default'
                          }
                        />
                      </Stack>
                    </CardContent>
                  )}

                {/* UPDATED SECTION */}
                {(history.action === 'updated' ||
                  history.action === 'status_changed') &&
                  history.updatedFields &&
                  Object.keys(history.updatedFields).filter(
                    (key) => key !== '_details'
                  ).length > 0 && (
                    <CardContent
                      className="audit-trail-content"
                      sx={{ px: 0, pb: 0 }}
                    >
                      {!history.updatedFields.approvalStatus && (
                        <Stack direction="row" spacing={2}>
                          <Typography variant="body1" component="span">
                            Status changed from
                          </Typography>
                          <Chip label={history.previousStatus || 'Pending'} />
                          <Typography variant="body1" component="span">
                            to
                          </Typography>
                          <Chip label={history.newStatus} />
                        </Stack>
                      )}

                      <Stack direction="column" spacing={2} mt={2}>
                        <Typography variant="body2" fontWeight={500}>
                          Changes Made:
                        </Typography>

                        {Object.keys(history.updatedFields)
                          .filter((key) => key !== '_details')
                          .map((field) => (
                            <Box key={field} className="change-block">
                              <Typography variant="body2" fontWeight={500}>
                                {getFieldDisplayName(field, history.entityType)}
                                :
                              </Typography>

                              <Stack direction="row" spacing={1} mt={0.5}>
                                <Typography
                                  variant="body2"
                                  className="text-gray"
                                >
                                  From:
                                </Typography>
                                <Typography
                                  variant="body2"
                                  className="text-gray"
                                >
                                  {formatValue(
                                    history.updatedFields?.[field]?.old
                                  )}
                                </Typography>
                              </Stack>

                              <Stack direction="row" spacing={1}>
                                <Typography
                                  variant="body2"
                                  className="text-gray"
                                >
                                  To:
                                </Typography>
                                <Typography
                                  variant="body2"
                                  className="text-gray"
                                >
                                  {formatValue(
                                    history.updatedFields?.[field]?.new
                                  )}
                                </Typography>
                              </Stack>
                            </Box>
                          ))}
                      </Stack>
                    </CardContent>
                  )}

                {/* CREATED SECTION */}
                {history.action === 'created' && history.updatedFields && (
                  <Grid container spacing={2} mt={2}>
                    {Object.keys(history.updatedFields)
                      .filter((key) => key !== '_details')
                      .map((field) => (
                        <>
                          {formatCreatedValue(
                            history.updatedFields?.[field]?.new
                          ) !== '' &&
                            getFieldDisplayName(field, history.entityType) !==
                              '' && (
                              <>
                                <Grid item xs={4}>
                                  <Typography fontWeight={600}>
                                    {getFieldDisplayName(
                                      field,
                                      history.entityType
                                    )}
                                  </Typography>
                                </Grid>
                                <Grid item xs={8}>
                                  <Typography>
                                    {formatCreatedValue(
                                      history.updatedFields?.[field]?.new
                                    )}
                                  </Typography>
                                </Grid>
                              </>
                            )}
                        </>
                      ))}
                  </Grid>
                )}
              </CardContent>
            </Card>
          ))
        )}
      </Box>
    </Box>
  );
};

export default AuditTrail;
